// SAMPLE CONTENT. Replace each document with the approved text from the legal team.
// Later this can come from the API: fetch it in the page component and pass it to <LegalPage doc={...} />.
//
// Shape of a document:
//   body items are either a string (a paragraph) or an array of strings (a bullet list).

export const privacyPolicy = {
  isSample: true,
  eyebrow: 'Legal',
  title: 'Privacy policy',
  summary:
    'How Ethosboard collects, uses and protects information when you use the platform.',
  lastUpdated: '2026-11-04',
  version: '0.1 (draft)',
  highlights: [
    'We collect only what we need to run learning for your organization.',
    'Your organization decides who can see learner data.',
    'You can ask us to access, correct or delete your data.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'This policy explains what personal information Ethosboard, a product of Infylearn Technologies, handles and why. It applies to learners, managers and administrators who use the platform.',
        'Your organization is the controller of learner data it adds to the platform. We process that data on its behalf.',
      ],
    },
    {
      id: 'information-we-collect',
      title: 'Information we collect',
      body: [
        'We collect information in three ways:',
        [
          'Account details, such as name, work email, role and organization.',
          'Learning activity, such as course enrollments, progress, assessment results and certificates.',
          'Usage data, such as device type, browser and pages visited, collected through cookies and logs.',
        ],
      ],
    },
    {
      id: 'how-we-use-information',
      title: 'How we use information',
      body: [
        'We use information to deliver courses, track progress, show readiness and skills, send notifications you have asked for, and keep the platform secure.',
        'We also use aggregated, non-identifying data to improve the product.',
      ],
    },
    {
      id: 'ai-features',
      title: 'AI features',
      body: [
        'EthosAI features, such as the briefing, chat and Learning Buddy, use platform data to answer questions and draft suggestions. Access follows the same permissions as the rest of the platform.',
        'Questions and the data needed to answer them may be processed by an AI provider. [Final provider list and retention to be confirmed.]',
      ],
    },
    {
      id: 'sharing',
      title: 'Sharing and service providers',
      body: [
        'We do not sell personal information. We share it only with:',
        [
          'Your organization, according to the permissions it has set.',
          'Service providers that host, secure or support the platform, under written agreements.',
          'Authorities, when the law requires it.',
        ],
      ],
    },
    {
      id: 'cookies',
      title: 'Cookies',
      body: [
        'We use essential cookies to keep you signed in and optional cookies to understand how the site is used. You can change your cookie choices in your browser settings.',
      ],
    },
    {
      id: 'retention',
      title: 'Data retention',
      body: [
        'We keep information for as long as your organization has an active account and for a limited period afterwards, unless the law requires longer. [Retention periods to be confirmed.]',
      ],
    },
    {
      id: 'security',
      title: 'Security',
      body: [
        'We protect information with access controls, encryption in transit and regular reviews. No system is perfectly secure, so we also limit how much data we keep.',
      ],
    },
    {
      id: 'your-rights',
      title: 'Your rights',
      body: [
        'Depending on where you live, you may have the right to:',
        [
          'Access the information we hold about you.',
          'Correct information that is wrong.',
          'Delete your information or restrict how it is used.',
          'Object to certain processing or withdraw consent.',
        ],
        'Learners should first contact their organization administrator. You can also write to us using the contact details below.',
      ],
    },
    {
      id: 'changes',
      title: 'Changes to this policy',
      body: [
        'We will update the date at the top of this page when the policy changes, and tell organization administrators about material changes.',
      ],
    },
  ],
  contact: {
    heading: 'Questions about your data?',
    text: 'Write to our privacy contact and we will reply as soon as we can.',
    email: 'privacy@example.com', // sample address
  },
};

export const termsOfUse = {
  isSample: true,
  eyebrow: 'Legal',
  title: 'Terms of use',
  summary:
    'The rules for using Ethosboard, for organizations and for the people they invite.',
  lastUpdated: '2026-11-04',
  version: '0.1 (draft)',
  highlights: [
    'Organizations are responsible for the users and content they add.',
    'Use the platform lawfully and keep your account secure.',
    'Either side can end the agreement as set out below.',
  ],
  sections: [
    {
      id: 'acceptance',
      title: 'Acceptance of these terms',
      body: [
        'By signing in to or using Ethosboard you agree to these terms. If you use it on behalf of an organization, you confirm you can bind that organization.',
      ],
    },
    {
      id: 'accounts',
      title: 'Accounts and access',
      body: [
        'Accounts are created by your organization administrator. Keep your sign-in details private and tell us promptly if you think your account has been misused.',
        'Administrators decide which learners, managers and instructors can access which areas.',
      ],
    },
    {
      id: 'licences',
      title: 'Licences and seats',
      body: [
        'Access is provided under the number of seats agreed with your organization. Extra seats need an approved request. [Commercial terms to be confirmed.]',
      ],
    },
    {
      id: 'acceptable-use',
      title: 'Acceptable use',
      body: [
        'You agree not to:',
        [
          'Break the law or infringe anyone else\'s rights.',
          'Upload harmful code or attempt to disrupt or probe the platform.',
          'Share content that is abusive, misleading or confidential without permission.',
          'Use the platform to build a competing product.',
        ],
      ],
    },
    {
      id: 'content',
      title: 'Content and ownership',
      body: [
        'Your organization keeps ownership of the courses, learner data and other content it adds. It gives us the right to host and process that content to run the service.',
        'Ethosboard, its design and its software remain the property of Infylearn Technologies.',
      ],
    },
    {
      id: 'ai-features',
      title: 'AI features',
      body: [
        'AI answers can be wrong. Check important information before acting on it. Actions suggested by EthosAI only run after a person confirms them.',
      ],
    },
    {
      id: 'availability',
      title: 'Availability and support',
      body: [
        'We work to keep the platform available but cannot promise it will never be interrupted. We will give notice of planned maintenance where we can. [Service levels to be confirmed.]',
      ],
    },
    {
      id: 'termination',
      title: 'Suspension and termination',
      body: [
        'We may suspend access that breaks these terms or puts the platform at risk. Organizations can end their agreement as described in their contract, and can export their data before it ends.',
      ],
    },
    {
      id: 'liability',
      title: 'Disclaimers and liability',
      body: [
        'The platform is provided as is to the extent the law allows. Our liability is limited as set out in your agreement. [Final wording and governing law to be confirmed.]',
      ],
    },
    {
      id: 'changes',
      title: 'Changes to these terms',
      body: [
        'We may update these terms. We will change the date at the top of this page and tell organization administrators about material changes.',
      ],
    },
  ],
  contact: {
    heading: 'Questions about these terms?',
    text: 'Contact us and we will point you to the right person.',
    email: 'legal@example.com', // sample address
  },
};